import { IS_DEVELOPMENT, IS_PRODUCTION_DEPLOYMENT, ENVIRONMENT } from './env.js';
import { InternalChain } from './enums.js';
import { exhaustiveGuard } from '../helpers.js';

const CLOUDFRONT_URI_PROD = "d2i9ybouka0ieh.cloudfront.net";
const CLOUDFRONT_URI_NON_PROD = "diszlt9l6ug2m.cloudfront.net";
const CLOUDFRONT_URI = IS_PRODUCTION_DEPLOYMENT ? CLOUDFRONT_URI_PROD : CLOUDFRONT_URI_NON_PROD;
const BUNDLER_NODE = IS_PRODUCTION_DEPLOYMENT ? ["https://node1.bundlr.network", "https://node2.bundlr.network"] : ["https://devnet.bundlr.network"];
const BUNDLER_NETWORK_TX_READ = IS_PRODUCTION_DEPLOYMENT ? "https://gateway.bundlr.network" : "https://devnet.bundlr.network";
const DEV_EMAIL = "dev@sound.xyz";
const MAX_UPLOAD_FILE_SIZE_BYTES = 104857600;
const MAX_UPLOAD_FILE_SIZE_TEXT = "100MB";
const MAX_AUDIO_UPLOAD_SIZE_BYTES = 314572800;
const MAX_AUDIO_UPLOAD_SIZE_TEXT = "300MB";
const MIN_MULTI_PART_COUNT = 1;
const MAX_MULTI_PART_COUNT = 30;
const MULTI_PART_UPLOAD_EXPIRY_SECONDS = 900;
const SECONDARY_SALE_ARTIST_ROYALTY_PERCENTAGE = 0.1;
const OPEN_SEA_ASSET_CONTRACT_API = IS_PRODUCTION_DEPLOYMENT ? "https://api.opensea.io/api/v1/asset_contract" : `https://testnets-api.opensea.io/api/v1/asset_contract`;
const OPEN_SEA_COLLECTION_API = IS_PRODUCTION_DEPLOYMENT ? "https://api.opensea.io/api/v1/collection" : `https://testnets-api.opensea.io/api/v1/collection`;
const networkNames = {
  1: "mainnet",
  5: "goerli",
  10: "optimism",
  420: "optimism-goerli",
  8453: "base",
  84531: "base-goerli"
};
const chainIds = {
  MAINNET: 1,
  GOERLI: 5,
  OPTIMISM: 10,
  OPTIMISM_GOERLI: 420,
  BASE: 8453,
  BASE_GOERLI: 84531
};
const ChainNames = Object.values({
  MAINNET: "ethereum",
  GOERLI: "ethereum",
  OPTIMISM: "optimism",
  OPTIMISM_GOERLI: "optimism",
  BASE: "base",
  BASE_GOERLI: "base"
});
const supportedChainIds = [
  1,
  // Mainet
  5,
  // Goerli
  10,
  // Optimism
  420,
  // Optimism Goerli
  8453,
  // Base
  84531
  // Base Goerli
];
function supportedChainIdOrThrow(chainId) {
  if (supportedChainIds.includes(chainId)) {
    return chainId;
  }
  throw new Error("The chainId is not supported: " + chainId);
}
const ROYALTY_BPS = 1e3;
const SAM_ARTIST_FEE_BPS = 500;
const DEFAULT_PRICE_OPTIONS = [5e-3, 0.01];
const DEFAULT_MINTING_STARTS_IN_DAYS = [7, 14];
const OPEN_EDITION_MINTING_PERIOD_DAYS = [3, 7];
const FIXED_EDITION_QUANTITY_OPTIONS_DEFAULT = [
  10,
  11,
  12,
  13,
  14,
  15,
  16,
  17,
  18,
  19,
  20,
  21,
  22,
  23,
  24,
  25,
  26,
  27,
  28,
  29,
  30,
  31,
  32,
  33,
  34,
  35,
  36,
  37,
  38,
  39,
  40,
  41,
  42,
  43,
  44,
  45,
  46,
  47,
  48,
  49,
  50
];
const USER_TOP_FOLLOWERS_LENGTH = 3;
const FIXED_EDITION_PRICE_OPTIONS_DEFAULT = [
  0,
  0.01,
  0.02,
  0.03,
  0.04,
  0.05,
  0.06,
  0.07,
  0.08,
  0.09,
  0.1
];
const FIXED_AUCTION_MIN_QUANTITY_RANGE = 5;
const FIXED_AUCTION_MAX_QUANTITY_RANGE = 1e4;
const OPEN_EDITION_PRICE_OPTIONS_DEFAULT = [
  0.01,
  0.02,
  0.03,
  0.04,
  0.05,
  0.06,
  0.07,
  0.08,
  0.09,
  0.1
];
const OPEN_EDITION_MIN_OPTIONS_DEFAULT = [25];
const OPEN_EDITION_LENGTH_DAYS_DEFAULT = [1, 3, 5, 7];
const MINT_QUANTITY = ENVIRONMENT === "production" ? 25 : 5;
const rangeBoundEditionQuantities = (quantity) => {
  return IS_PRODUCTION_DEPLOYMENT ? [25, 50] : (
    // to account for smaller mint options selected
    [quantity, 2 * quantity]
  );
};
const RETURNING_ARTIST_QUANTITY_OPTIONS = IS_PRODUCTION_DEPLOYMENT ? [25] : [1, 2, 3, 4, 5, 25];
const MINT_PRICE = 0.1;
const SEGMENT_API_BASE_TRACK = "https://api.segment.io/v1/track";
const SEGMENT_API_PUBLIC_WRITE_KEY = process.env.SEGMENT_API_PUBLIC_WRITE_KEY;
const MAX_WAVEFORM_COMMENTS = 100;
const SOUND_BASE_URI = IS_DEVELOPMENT ? "http://localhost:3000" : IS_PRODUCTION_DEPLOYMENT ? "https://www.sound.xyz" : `https://${process.env.NEXT_PUBLIC_VERCEL_URL}`;
const FEATURED_SOUNDS_MAX_LENGTH = 1;
const DEFAULT_SHELF_NAME = "My Collection";
const SHELF_MAX_TITLE_LENGTH = 36;
const SHELF_MAX_DESCRIPTION_LENGTH = 144;
const ARTIST_COLLECTOR_SECTION_LENGTH = 10;
const NULL_ADDRESS = "0x0000000000000000000000000000000000000000";
const PAPER_CLIP_TROLL_ADDRESS = "0xaad7e627747db779b447a6552fec63fa4e0afdfa";
const DEAD_ADDRESS = "0x000000000000000000000000000000000000dead";
const BURNED_ADDRESSES = new Set(
  [NULL_ADDRESS, PAPER_CLIP_TROLL_ADDRESS, DEAD_ADDRESS].map(
    (a) => a.toLowerCase()
  )
);
const SQL_BURNED_ADDRESS_GROUP = `'${Array.from(BURNED_ADDRESSES).join(
  `','`
)}'`;
const NON_NULL_ADDRESS = "0x0000000000000000000000000000000000000001";
const MAX_UINT32 = 4294967295;
const LOCAL_METADATA_BASE_URI = "http://localhost:8396/";
const MIN_MAX_MINTS_PER_WALLET = 1;
const AUCTION_MIN_PRICE_RANGE = 0;
const AUCTION_MAX_PRICE_RANGE = 1;
const SAM_CONFIGURATION_DEFAULT_INFLECTION_COUNT = 1500;
const SAM_CONFIGURATION_DEFAULT_LINEAR_PRICE_SLOPE = "0";
const SPLIT_PERCENTAGE_SCALE = 1e6;
const SOUND_MARKET_URL = "https://market.sound.xyz";
const RELEASE_BACKERS_CSV_S3_FILENAME = "backers.csv";
const MintStartTimeBufferSeconds = {
  PROD: 3600,
  DEV: 120
};
const DUPLICATE_INPUT_ENUM_ARRAY_ERROR_MESSAGE = (field) => `Filter enum array \`${field}\` can not contain duplicate values`;
const DUPLICATE_INPUT_ARRAY_ERROR_MESSAGE = (field) => `Filter array \`${field}\` can not contain duplicate values`;
const CHARTS_SIZE = 100;
const TOP_COLLECTORS_BADGE_SIZE = 200;
const MULTI_SELECT_ENTITIY_LIMIT = 100;
const ACTIVITY_FEED_GROUP_MAX_COLLECTOR_ACTIONS = 3;
const ACTIVITY_FEED_GROUP_MAX_NUM_ACTIONS = 50;
const RELEASE_SOCIAL_PROOF_NUM_USERS = 3;
const OPEN_EDITION_NUMBERED_METADATA_COUNT = 1e3;
const OPEN_EDITION_SONG_SLOTS = 100;
const SONG_SLOT_MAX_LENGTH = 100;
const MAX_SAFE_NUMBER_JS = 9007199254740991;
const MIN_SAFE_NUMBER_JS = -9007199254740991;
const MAX_INT_32 = 2147483647;
const OPEN_EDITION_MAX_MINTABLE = MAX_INT_32;
const PAPER_API_BASE = "https://withpaper.com/api";
const SDK_SOUND_ATTRIBUTON_ID = "100034050823429212663744024974472219327326309225212339598659123908725914828493";
function getChainName(chainId) {
  switch (chainId) {
    case chainIds.MAINNET:
      return InternalChain.MAINNET;
    case chainIds.GOERLI:
      return InternalChain.GOERLI;
    case chainIds.OPTIMISM:
      return InternalChain.OPTIMISM;
    case chainIds.OPTIMISM_GOERLI:
      return InternalChain.OPTIMISM_GOERLI;
    case chainIds.BASE:
      return InternalChain.BASE;
    case chainIds.BASE_GOERLI:
      return InternalChain.BASE_GOERLI;
    default:
      throw new Error(`Unexpected chainId ${chainId}`);
  }
}
function getPaperContractIds(chain) {
  switch (chain) {
    case "GOERLI": {
      return {
        merkleDropMinterV2: "75932aa0-b495-448e-a09c-0da055b0c123",
        merkleDropMinterV2_1: "5de03160-27e1-4ebd-a9aa-7f3992eb2777",
        rangeEditionMinterV2: "4981927b-7d79-4df5-9d08-cf8e3cfa5a6a",
        rangeEditionMinterV2_1: "a6f71c03-b570-4a13-adad-44531a948265",
        soundSwap: ENVIRONMENT === "staging" ? "da3b85b8-3613-4ea4-9c84-a1b073422add" : "1819e396-0d98-44f5-9e8f-b63b0cb06da3",
        soundSwapV1_1: "af041e8b-b9b2-4d42-8cfd-aed16ed5d000",
        minterAdapter: "1b0e21be-4ac5-445d-8ca7-10970a32fe11"
      };
    }
    case "MAINNET": {
      return {
        soundSwap: "115b122b-bce0-4158-ace1-1a7c254e2e15",
        rangeEditionMinterV2: "5ec66b98-87d8-4864-8f9e-e5cadb54fd9f",
        merkleDropMinterV2: "82bfcefe-6e66-48d4-9429-04c724e2167b",
        minterAdapter: "892f3158-02a7-4d32-b33a-42bf72fdb46c",
        merkleDropMinterV2_1: "c21045f1-0c41-4d6b-a50e-8c25d5c9b16d",
        soundSwapV1_1: "e78a99da-6b8b-4578-bc8a-61df70c227c2",
        rangeEditionMinterV2_1: "9adfd0fc-06cb-46a5-80da-a2d061c82c13"
      };
    }
    case "OPTIMISM": {
      return {
        merkleDropMinterV2_1: "d8f249b3-4f4d-45e2-9a21-67aff462b50a",
        rangeEditionMinterV2_1: "d47328fe-ce78-414d-990c-b119d159e2f0",
        soundSwap: "ac693f3d-8fe3-4c77-aa4c-729c4b662b49",
        rangeEditionMinterV2: "f354fc9d-615e-41c5-9d55-4feac597ac5a",
        merkleDropMinterV2: "34c052f9-6f3b-44cd-8c20-a997e014eac6",
        soundSwapV1_1: "0754828a-c36e-4a88-b7f9-803a77fd291c",
        minterAdapter: null
      };
    }
    case "OPTIMISM_GOERLI": {
      return {
        soundSwap: "fdc501ea-4124-4a9d-9893-5364c3d6a6c0",
        rangeEditionMinterV2: "c301a516-7232-4a21-ab47-80603cc24341",
        merkleDropMinterV2: "6ea86833-1b50-4138-8a62-d7590ed398db",
        merkleDropMinterV2_1: "09d6f72a-8d33-4904-8077-338f2e0ce32c",
        rangeEditionMinterV2_1: "404e2869-ba4e-4880-a7b1-e30afc4c49e1",
        soundSwapV1_1: "32377898-da9e-4519-bec6-f1a6ef5bc9ac",
        minterAdapter: null
      };
    }
    case "BASE":
    case "BASE_GOERLI":
      return null;
    default:
      exhaustiveGuard(chain);
  }
}
const ACR_CLOUD_API_BASE = "https://api-v2.acrcloud.com/api";
const ACR_CLOUD_FILESCAN_ENDPOINT = `${ACR_CLOUD_API_BASE}/fs-containers`;
const SPOTIFY_BASE_URL = "https://open.spotify.com/";
const NULL_BYTES32 = "0x0000000000000000000000000000000000000000000000000000000000000000";
function isNullishHex(value) {
  return value === NULL_BYTES32 || value === NULL_ADDRESS || value === "0x";
}
const MIN_WITHDRAWABLE_WEI = BigInt(1e14);
function isWithdrawableBalance(balance) {
  return BigInt(balance) >= MIN_WITHDRAWABLE_WEI;
}

export { ACR_CLOUD_API_BASE, ACR_CLOUD_FILESCAN_ENDPOINT, ACTIVITY_FEED_GROUP_MAX_COLLECTOR_ACTIONS, ACTIVITY_FEED_GROUP_MAX_NUM_ACTIONS, ARTIST_COLLECTOR_SECTION_LENGTH, AUCTION_MAX_PRICE_RANGE, AUCTION_MIN_PRICE_RANGE, BUNDLER_NETWORK_TX_READ, BUNDLER_NODE, BURNED_ADDRESSES, CHARTS_SIZE, CLOUDFRONT_URI, CLOUDFRONT_URI_NON_PROD, CLOUDFRONT_URI_PROD, ChainNames, DEFAULT_MINTING_STARTS_IN_DAYS, DEFAULT_PRICE_OPTIONS, DEFAULT_SHELF_NAME, DEV_EMAIL, DUPLICATE_INPUT_ARRAY_ERROR_MESSAGE, DUPLICATE_INPUT_ENUM_ARRAY_ERROR_MESSAGE, FEATURED_SOUNDS_MAX_LENGTH, FIXED_AUCTION_MAX_QUANTITY_RANGE, FIXED_AUCTION_MIN_QUANTITY_RANGE, FIXED_EDITION_PRICE_OPTIONS_DEFAULT, FIXED_EDITION_QUANTITY_OPTIONS_DEFAULT, LOCAL_METADATA_BASE_URI, MAX_AUDIO_UPLOAD_SIZE_BYTES, MAX_AUDIO_UPLOAD_SIZE_TEXT, MAX_INT_32, MAX_MULTI_PART_COUNT, MAX_SAFE_NUMBER_JS, MAX_UINT32, MAX_UPLOAD_FILE_SIZE_BYTES, MAX_UPLOAD_FILE_SIZE_TEXT, MAX_WAVEFORM_COMMENTS, MINT_PRICE, MINT_QUANTITY, MIN_MAX_MINTS_PER_WALLET, MIN_MULTI_PART_COUNT, MIN_SAFE_NUMBER_JS, MIN_WITHDRAWABLE_WEI, MULTI_PART_UPLOAD_EXPIRY_SECONDS, MULTI_SELECT_ENTITIY_LIMIT, MintStartTimeBufferSeconds, NON_NULL_ADDRESS, NULL_ADDRESS, NULL_BYTES32, OPEN_EDITION_LENGTH_DAYS_DEFAULT, OPEN_EDITION_MAX_MINTABLE, OPEN_EDITION_MINTING_PERIOD_DAYS, OPEN_EDITION_MIN_OPTIONS_DEFAULT, OPEN_EDITION_NUMBERED_METADATA_COUNT, OPEN_EDITION_PRICE_OPTIONS_DEFAULT, OPEN_EDITION_SONG_SLOTS, OPEN_SEA_ASSET_CONTRACT_API, OPEN_SEA_COLLECTION_API, PAPER_API_BASE, RELEASE_BACKERS_CSV_S3_FILENAME, RELEASE_SOCIAL_PROOF_NUM_USERS, RETURNING_ARTIST_QUANTITY_OPTIONS, ROYALTY_BPS, SAM_ARTIST_FEE_BPS, SAM_CONFIGURATION_DEFAULT_INFLECTION_COUNT, SAM_CONFIGURATION_DEFAULT_LINEAR_PRICE_SLOPE, SDK_SOUND_ATTRIBUTON_ID, SECONDARY_SALE_ARTIST_ROYALTY_PERCENTAGE, SEGMENT_API_BASE_TRACK, SEGMENT_API_PUBLIC_WRITE_KEY, SHELF_MAX_DESCRIPTION_LENGTH, SHELF_MAX_TITLE_LENGTH, SONG_SLOT_MAX_LENGTH, SOUND_BASE_URI, SOUND_MARKET_URL, SPLIT_PERCENTAGE_SCALE, SPOTIFY_BASE_URL, SQL_BURNED_ADDRESS_GROUP, TOP_COLLECTORS_BADGE_SIZE, USER_TOP_FOLLOWERS_LENGTH, chainIds, getChainName, getPaperContractIds, isNullishHex, isWithdrawableBalance, networkNames, rangeBoundEditionQuantities, supportedChainIdOrThrow, supportedChainIds };
